/**
 * Step components used by AccountFormWizard (Identity + Settings + Bank).
 * Extracted to a sibling file to keep AccountFormWizard.tsx under the
 * max-lines threshold. Not part of the public API.
 */

import { useState, useMemo, useRef, useEffect } from 'react'

import { Input, Select, FormField, Checkbox } from '@elsapiens/ui'
import { AlertTriangle, CheckCircle2 } from 'lucide-react'

import { computeNextSubCode, SiblingsPopup, TagsField } from './AccountFormWizard-parts'
import { type AccountFormData } from './chart-of-accounts-helpers'
import { ACCOUNT_SUBTYPE_OPTIONS } from '../../constants/labels'
import { TYPE_PREFIXES } from '../../constants/reserved-account-codes'

import type { Account } from '../../services/account-service'

export type AccountIntent = 'cash' | 'bank' | 'general'

const CASH_SUBTYPES = ACCOUNT_SUBTYPE_OPTIONS.filter(o => o.value === 'Cash' || o.value === 'Petty Cash')
const BANK_SUBTYPES = ACCOUNT_SUBTYPE_OPTIONS.filter(o => o.value.startsWith('Bank'))

// ── IdentityStep ──────────────────────────────────────────────────────────────

export interface IdentityStepProps {
  formData: AccountFormData
  setFormData: React.Dispatch<React.SetStateAction<AccountFormData>>
  intent: AccountIntent
  codeError: string | undefined
  codeWarning: string | undefined
  parentOptions: Array<{ value: string; label: string }>
  accountsMap: Map<string, Account>
  selectedParent: Account | undefined
  isEntityControlParent: boolean
}

function AutoManagedNotice({ selectedParent }: { selectedParent: Account | undefined }): JSX.Element {
  return (
    <div className="el-edge rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/30 p-3 flex items-start gap-2.5">
      <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
      <div>
        <p className="text-sm font-medium text-amber-800 dark:text-amber-300">Auto-managed ledger</p>
        <p className="text-sm text-amber-700 dark:text-amber-400 mt-0.5">
          Sub-accounts under <strong>{selectedParent?.code} – {selectedParent?.name}</strong> are created automatically
          when customers or suppliers are added. Select a different parent.
        </p>
      </div>
    </div>
  )
}

function IdentityHelp(): JSX.Element {
  return (
    <div className="el-edge flex items-start gap-2.5 p-3 rounded-lg border border-muted bg-muted/30">
      <CheckCircle2 className="w-4 h-4 text-muted-foreground shrink-0 mt-0.5" />
      <p className="text-xs text-muted-foreground leading-relaxed">
        The <strong>code</strong> is a short unique identifier used in transactions and reports (e.g. <em>1010</em>).
        The <strong>name</strong> is the human-readable label shown everywhere in the system.
      </p>
    </div>
  )
}

interface IdentityFieldsProps {
  formData: AccountFormData
  setFormData: React.Dispatch<React.SetStateAction<AccountFormData>>
  isCashOrBank: boolean
  codeError: string | undefined
  codeWarning: string | undefined
  prefixHint: string | undefined
  nextCode: string | null
  firstFieldRef: React.RefObject<HTMLInputElement>
}

function pickCodeHelperText(isCashOrBank: boolean, codeError: string | undefined, prefixHint: string | undefined): string | undefined {
  if (isCashOrBank) {return 'Auto-generated'}
  if (codeError) {return undefined}
  return prefixHint
}

function CodeWarningNotice({ codeWarning }: { codeWarning: string }): JSX.Element {
  return (
    <div
      className="col-span-2 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-900/20 px-3 py-2 text-sm text-amber-800 dark:text-amber-300"
      data-testid="code-warning"
    >
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
      <span>{codeWarning}. You can still proceed — this is a system-reserved code and may conflict with automated features.</span>
    </div>
  )
}

function IdentityCodeAndName({ formData, setFormData, isCashOrBank, codeError, codeWarning, prefixHint, nextCode, firstFieldRef }: IdentityFieldsProps): JSX.Element {
  return (
    <div className="grid el-gap-field" style={{ gridTemplateColumns: '1fr 2fr' }}>
      <FormField
        required
        error={codeError}
        helperText={pickCodeHelperText(isCashOrBank, codeError, prefixHint)}
        label="Account Code"
      >
        <Input
          ref={isCashOrBank ? undefined : firstFieldRef}
          className={isCashOrBank ? 'bg-muted text-muted-foreground cursor-default' : undefined}
          placeholder={nextCode ?? 'e.g., 1000'}
          readOnly={isCashOrBank}
          value={formData.code}
          onChange={isCashOrBank ? undefined : e => setFormData(prev => ({ ...prev, code: e.target.value }))}
        />
      </FormField>
      <FormField required helperText="Shown in reports, transaction forms, and statements" label="Account Name">
        <Input
          ref={isCashOrBank ? firstFieldRef : undefined}
          placeholder="e.g., Cash and Cash Equivalents"
          value={formData.name}
          onChange={e => setFormData(prev => ({ ...prev, name: e.target.value }))}
        />
      </FormField>
      {codeWarning && <CodeWarningNotice codeWarning={codeWarning} />}
    </div>
  )
}

interface SiblingsRowProps {
  formData: AccountFormData
  setFormData: React.Dispatch<React.SetStateAction<AccountFormData>>
  isCashOrBank: boolean
  nextCode: string | null
  siblings: Account[]
}

function SiblingsRow({ formData, setFormData, isCashOrBank, nextCode, siblings }: SiblingsRowProps): JSX.Element {
  const visibleSiblings = siblings.slice(0, 3)
  const hiddenCount = siblings.length - 3
  return (
    <div className="flex items-center gap-1.5 overflow-hidden">
      {!isCashOrBank && nextCode && formData.code !== nextCode && (
        <button
          className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium border border-primary/40 bg-primary/5 text-primary hover:bg-primary/10 transition-colors"
          type="button"
          onClick={() => setFormData(prev => ({ ...prev, code: nextCode }))}
        >
          Next: {nextCode}
        </button>
      )}
      {siblings.length > 0 && (
        <>
          <span className="text-xs text-muted-foreground">Used:</span>
          {visibleSiblings.map(s => (
            <span key={s.id} className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-mono bg-muted text-muted-foreground border border-border whitespace-nowrap">
              {s.code}
            </span>
          ))}
          {hiddenCount > 0 && <SiblingsPopup count={hiddenCount} siblings={siblings} />}
        </>
      )}
    </div>
  )
}

function useIdentityCodeAutoFill(args: {
  isCashOrBank: boolean
  selectedParent: Account | undefined
  siblings: Account[]
  formData: AccountFormData
  setFormData: React.Dispatch<React.SetStateAction<AccountFormData>>
  nextCode: string | null
}): void {
  const { isCashOrBank, selectedParent, siblings, formData, setFormData, nextCode } = args
  // For cash/bank: re-compute code whenever parent changes (always read-only)
  useEffect(() => {
    if (!isCashOrBank || !selectedParent) {return}
    const next = computeNextSubCode(selectedParent.code, siblings) ?? ''
    setFormData(prev => prev.code === next ? prev : { ...prev, code: next })
  }, [isCashOrBank, formData.parentId, siblings, setFormData]) // eslint-disable-line react-hooks/exhaustive-deps

  // For general: auto-fill code only when parent changes
  const prevParentRef = useRef(formData.parentId)
  useEffect(() => {
    if (isCashOrBank) {return}
    if (formData.parentId === prevParentRef.current) {return}
    prevParentRef.current = formData.parentId
    setFormData(prev => ({ ...prev, code: nextCode ?? '' }))
  }, [formData.parentId, nextCode, isCashOrBank, setFormData])
}

function useIdentityStepState(args: {
  formData: AccountFormData
  setFormData: React.Dispatch<React.SetStateAction<AccountFormData>>
  intent: AccountIntent
  accountsMap: Map<string, Account>
  selectedParent: Account | undefined
}): {
  isCashOrBank: boolean
  siblings: Account[]
  nextCode: string | null
  prefixHint: string | undefined
} {
  const { formData, setFormData, intent, accountsMap, selectedParent } = args
  const isCashOrBank = intent === 'cash' || intent === 'bank'
  const siblings = useMemo(
    () => Array.from(accountsMap.values()).filter(a => a.parentId === formData.parentId),
    [accountsMap, formData.parentId],
  )
  const nextCode = useMemo(() => {
    if (!selectedParent) {return null}
    return computeNextSubCode(selectedParent.code, siblings)
  }, [selectedParent, siblings])

  useIdentityCodeAutoFill({ isCashOrBank, selectedParent, siblings, formData, setFormData, nextCode })

  const prefixHint = selectedParent
    ? undefined
    : `Must start with ${(TYPE_PREFIXES[formData.accountType] ?? []).join(' or ')}`

  return { isCashOrBank, siblings, nextCode, prefixHint }
}

function ParentSelectField({ formData, setFormData, parentOptions }: { formData: AccountFormData; setFormData: React.Dispatch<React.SetStateAction<AccountFormData>>; parentOptions: Array<{ value: string; label: string }> }): JSX.Element {
  return (
    <FormField label="Parent Account">
      <Select
        options={parentOptions}
        value={formData.parentId}
        onChange={value => setFormData(prev => ({ ...prev, parentId: value }))}
      />
    </FormField>
  )
}

function HeaderCheckboxRow({ formData, setFormData }: { formData: AccountFormData; setFormData: React.Dispatch<React.SetStateAction<AccountFormData>> }): JSX.Element {
  return (
    <Checkbox
      hideMessage
      checked={formData.isHeader}
      label="This is a header account (can have child accounts)"
      onChange={(checked) => setFormData(prev => ({
        ...prev, isHeader: checked,
        accountSubtype: checked ? '' : prev.accountSubtype,
      }))}
    />
  )
}

export function IdentityStep(p: IdentityStepProps): JSX.Element {
  const firstFieldRef = useRef<HTMLInputElement>(null)
  useEffect(() => {
    const raf = requestAnimationFrame(() => { firstFieldRef.current?.focus() })
    return () => cancelAnimationFrame(raf)
  }, [])

  const { isCashOrBank, siblings, nextCode, prefixHint } = useIdentityStepState({
    formData: p.formData, setFormData: p.setFormData, intent: p.intent,
    accountsMap: p.accountsMap, selectedParent: p.selectedParent,
  })

  return (
    <div className="flex flex-col el-gap-field">
      <ParentSelectField formData={p.formData} parentOptions={p.parentOptions} setFormData={p.setFormData} />
      {p.isEntityControlParent && <AutoManagedNotice selectedParent={p.selectedParent} />}
      <IdentityCodeAndName
        codeError={p.codeError}
        codeWarning={p.codeWarning}
        firstFieldRef={firstFieldRef}
        formData={p.formData}
        isCashOrBank={isCashOrBank}
        nextCode={nextCode}
        prefixHint={prefixHint}
        setFormData={p.setFormData}
      />
      {p.selectedParent && (
        <SiblingsRow formData={p.formData} isCashOrBank={isCashOrBank} nextCode={nextCode} setFormData={p.setFormData} siblings={siblings} />
      )}
      {p.intent === 'general' && <HeaderCheckboxRow formData={p.formData} setFormData={p.setFormData} />}
      <IdentityHelp />
    </div>
  )
}

// ── SettingsStep ──────────────────────────────────────────────────────────────

export interface SettingsStepProps {
  formData: AccountFormData
  setFormData: React.Dispatch<React.SetStateAction<AccountFormData>>
  intent: AccountIntent
  currencies: Array<{ value: string; label: string }>
  tagInput: string
  setTagInput: (v: string) => void
}

function getSubtypeOptionsForIntent(intent: AccountIntent): Array<{ value: string; label: string }> | null {
  if (intent === 'cash') {return CASH_SUBTYPES.map(o => ({ value: o.value, label: o.label }))}
  if (intent === 'bank') {return BANK_SUBTYPES.map(o => ({ value: o.value, label: o.label }))}
  return null
}

export function SettingsStep({ formData, setFormData, intent, currencies, tagInput, setTagInput }: SettingsStepProps): JSX.Element {
  const subtypeOptions = getSubtypeOptionsForIntent(intent)

  return (
    <div className="flex flex-col el-gap-field">
      <FormField required label="Currency">
        <Select
          options={currencies}
          value={formData.currency}
          onChange={value => setFormData(prev => ({ ...prev, currency: value }))}
        />
      </FormField>
      {subtypeOptions && (
        <FormField required label="Account Subtype">
          <Select
            options={subtypeOptions}
            value={formData.accountSubtype}
            onChange={value => setFormData(prev => ({ ...prev, accountSubtype: value }))}
          />
        </FormField>
      )}
      <FormField label="Description">
        <Input
          placeholder="Optional description"
          value={formData.description}
          onChange={e => setFormData(prev => ({ ...prev, description: e.target.value }))}
        />
      </FormField>
      <TagsField
        formData={formData}
        setFormData={setFormData}
        setTagInput={setTagInput}
        tagInput={tagInput}
      />
    </div>
  )
}

// Re-exports for the wizard's own usage
export { type Account }

// Silence unused-state lint when state above is for hook compactness
void useState
